import React, {useEffect, useState} from 'react';
import { toast } from 'sonner';
import { useAuthStore } from '@store/authStore';
import { ROUTES } from '@/constants';
import { canAccessModule, canPerform, ACTIONS } from '@/lib/permissions';
import AppLayout from './AppLayout';
import Unauthorized from '@/pages/Unauthorized';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

// Skip MFA enrollment + redirect entirely when running against a dev backend
// — speeds up auth iteration and avoids prompting devs to enroll TOTP locally.
const IS_DEV = process.env.REACT_APP_ENVIRONMENT === 'DEV';

const ProtectedRoute = ({ children, redirectTo = ROUTES.HOME.path, requiredModule, requiredAction = ACTIONS.READ, ...appLayoutProps }) => {
  const { isAuthenticated, setAuthType, isLoading: authLoading, user, userProfile, initialize, isInitialized } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [mfaCheckLoading, setMfaCheckLoading] = useState(!isInitialized);
  const [shouldRedirectToMfa, setShouldRedirectToMfa] = useState(false);
  // Skip initial auth check delay if already initialized (prevents remount delays on navigation)
  const [initialAuthCheck, setInitialAuthCheck] = useState(!isInitialized);

  // Initialize auth store on mount
  useEffect(() => {
    if (!isInitialized) {
      initialize();
    }
  }, [initialize, isInitialized]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!initialAuthCheck && !isAuthenticated && isInitialized) {
      setAuthType('signin');
      navigate(redirectTo);
    }
  }, [isAuthenticated, setAuthType, initialAuthCheck, isInitialized, navigate, redirectTo]);

  // Wait for initial auth check to complete (important for OAuth callbacks)
  // Only wait on first load, not on subsequent navigations
  useEffect(() => {
    // Skip if already initialized
    if (isInitialized) {
      setInitialAuthCheck(false);
      return;
    }

    const checkInitialAuth = async () => {
      // Wait a bit for Supabase to process OAuth callback
      await new Promise(resolve => setTimeout(resolve, 500));
      setInitialAuthCheck(false);
    };

    checkInitialAuth();
  }, [isInitialized]);

  // Check MFA enrollment for authenticated users
  useEffect(() => {
    // Skip MFA entirely in local environment.
    if (IS_DEV) {
      setMfaCheckLoading(false);
      return;
    }

    // Skip MFA check if already initialized (user was already verified)
    if (isInitialized && !mfaCheckLoading) {
      return;
    }

    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!isAuthenticated) {
        setMfaCheckLoading(false);
        return;
      }

      try {
        // Check if user signed in with OAuth (Google/Microsoft)
        const isOAuthUser = session?.user?.app_metadata?.provider &&
                           ['google', 'azure'].includes(session.user.app_metadata.provider);

        // Skip MFA check for OAuth users
        if (isOAuthUser) {
          setMfaCheckLoading(false);
          return;
        }

        // Skip MFA check for internal @gotrustjet.com accounts. Kept in
        // lockstep with the SignIn bypass so the user isn't bounced back
        // to /mfa-enroll after landing on a protected route.
        const email = (session?.user?.email || '').toLowerCase();
        if (email.endsWith('@gotrustjet.com')) {
          setMfaCheckLoading(false);
          return;
        }

        // Check if MFA is enrolled for email/password users
        const { data: factors } = await supabase.auth.mfa.listFactors();
        if (!factors || factors.totp.length === 0) {
          // MFA not enrolled, set redirect flag
          setShouldRedirectToMfa(true);
        }

      } catch (error) {
        console.error('Error checking MFA factors:', error);
      } finally {
        setMfaCheckLoading(false);
      }
    };

    if (!initialAuthCheck) {
      checkUser();
    }
  }, [isAuthenticated, initialAuthCheck, isInitialized, mfaCheckLoading]);

  // Handle MFA redirect
  useEffect(() => {
    if (IS_DEV) return;
    if (shouldRedirectToMfa && !mfaCheckLoading) {
      navigate('/mfa-enroll');
    }
  }, [shouldRedirectToMfa, mfaCheckLoading, navigate]);

  // Enforce module-level access. Profile must be loaded so we can read `role`.
  // For requiredAction=read this reduces to a module read check; richer
  // actions (create/update/delete/sendQuote/generateInvoice) are also
  // honored so e.g. /generate-quote can require CREATE.
  const moduleDenied =
    !!requiredModule &&
    !!userProfile &&
    (
      !canAccessModule(userProfile.role, requiredModule) ||
      !canPerform(userProfile.role, requiredModule, requiredAction)
    );

  useEffect(() => {
    if (!moduleDenied) return;
    if (initialAuthCheck || mfaCheckLoading || authLoading) return;
    toast.error("You don't have access to this page.");
  }, [moduleDenied, initialAuthCheck, mfaCheckLoading, authLoading]);

  // Redirect to profile setup if user is authenticated but has no profile
  useEffect(() => {
    console.log("userProfile", userProfile);
    console.log("user", user);
    console.log("authLoading", authLoading);

    // Wait for all loading states to complete before checking profile
    if (initialAuthCheck || mfaCheckLoading || authLoading) return;

    // Check for both 'id' and 'userprofile_id' as the API may return either
    const hasProfile = userProfile && (userProfile.id || userProfile.userprofile_id);

    if (user && !hasProfile && location.pathname !== ROUTES.PROFILE_SETUP.path) {
      navigate(ROUTES.PROFILE_SETUP.path, { replace: true });
    }
  }, [user, userProfile, initialAuthCheck, mfaCheckLoading, authLoading, location.pathname, navigate]);

  // Show loading during initial auth check or MFA check
  if (initialAuthCheck || mfaCheckLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Don't render content if we're about to redirect to MFA
  if (shouldRedirectToMfa) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Don't render content if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Render an Unauthorized page (still inside AppLayout so navigation stays usable)
  // when the user lacks access to this module.
  if (moduleDenied) {
    return (
      <AppLayout {...appLayoutProps}>
        <Unauthorized />
      </AppLayout>
    );
  }

  // Render with AppLayout if authenticated and MFA is enrolled
  return (
    <AppLayout {...appLayoutProps}>
      {children}
    </AppLayout>
  );
};

export default ProtectedRoute;
